<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-title>Login</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content
      :fullscreen="true"
      :scroll-y="true"
      :scroll-x="false"
      force-overscroll
    >
      <ion-header collapse="condense">
        <ion-toolbar>
          <ion-title size="large">Login</ion-title>
        </ion-toolbar>
      </ion-header>

      <ion-content class="ion-padding">
        <div>
          <ion-item>
            <ValidatedInput>
              <ion-label>Username</ion-label>
              <ion-input
                placeholder="username"
                v-model="username"
                name="username"
                autocomplete="username"
              />
            </ValidatedInput>
          </ion-item>
          <ion-item>
            <ValidatedInput>
              <ion-label>Password</ion-label>
              <ion-input
                placeholder="password"
                v-model="password"
                type="password"
                name="password"
                autocomplete="current-password"
              />
            </ValidatedInput>
          </ion-item>
          <ion-item>
            <ValidatedInput
              :did-check="apiCheckSuccess !== null"
              :check-successful="apiCheckSuccess || false"
            >
              <ion-label>Api Url</ion-label>
              <ion-input
                placeholder="Api Url"
                v-model="apiUrl"
                :name="`api[${pwMgrUrl}]`"
              />
            </ValidatedInput>
          </ion-item>
          <ion-item>
            <ion-checkbox v-model="rememberMe" labelPlacement="end">Remember me</ion-checkbox>
          </ion-item>
          <ion-item>
            <ion-button
              @click="submit()"
              :disabled="!(apiCheckSuccess || false)"
              >Submit</ion-button
            >
          </ion-item>
        </div>
        <div>
          <b>Debug:</b><br />
          apiUrl: {{ apiUrl }}<br />
          check: {{ apiCheckSuccess }}<br />
          <br />
          VITE_API_PROT: {{ VITE_API_PROT }}<br />
          VITE_API_HOST: {{ VITE_API_HOST }}<br />
          VITE_API_PORT: {{ VITE_API_PORT }}<br />
          VITE_API_PATH: {{ VITE_API_PATH }}<br />
          VITE_QR_URL: {{ VITE_QR_URL }}<br />
          VITE_API_URL: {{ VITE_API_URL }}<br />
          BASE_URL: {{ BASE_URL }}<br />
          baseUrl(full): {{ getBaseUrl(true) }}<br />
          baseUrl(absolute): {{ getBaseUrl(false) }}<br />
          apiUrl: {{ getApiUrl(false) }}<br />
          qrUrl: {{ getQrUrl("SOME_BARCODE") }}<br />
        </div>
      </ion-content>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  useIonRouter,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonInput,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonLabel,
  IonItem,
} from "@ionic/vue";
import {
  defineComponent, onMounted,
  ref, watch,
} from "vue";

import { AllMyStorageApiClient } from "@/models/api";
import router from "@/router";
import { getApiUrl, getQrUrl, getBaseUrl } from "@/data/constants";
import ValidatedInput from "@/components/ValidatedInput.vue";
import { cutApiUrl } from "@/api/helper";
import { setAuthCookies } from "@/api/auth-cookies";
import env from "@/environmentVariables";
import { useApiStore } from "@/stores/api";
import { reconfigureSentry } from "@/sentry";
import { describeToken } from "@/helper/jwt";
import { clearMaster, storeMaster } from "@/helper/passwordKeyStore";

defineComponent({
  name: "HomePage",
});

const ionRouter = useIonRouter();
const username = ref<string | null>(null);
const password = ref<string | null>(null);
const rememberMe = ref<boolean>(false);
// the API url: used for connecting to the API.
// Default is to use the same host, but if you're not running a nginx reverse proxy or similar,
// you can provide a different host.
// Also useful for testing/development.
const apiUrl = ref<string>(getApiUrl());
// the password manager URL: "http://example.com", used so password manager can distinguish the api field value for different hosts.
const pwMgrUrl = ref<string>(`${location.protocol}//${location.host}`);
const VITE_API_PROT = ref<string>(env.VITE_API_PROT);
const VITE_API_HOST = ref<string>(env.VITE_API_HOST);
const VITE_API_PORT = ref<string>(env.VITE_API_PORT);
const VITE_API_PATH = ref<string>(env.VITE_API_PATH);
const VITE_QR_URL = ref<string>(env.VITE_QR_URL);
const VITE_API_URL = ref<string>(env.VITE_API_URL);
const BASE_URL = ref<string>(env.BASE_URL);
const apiCheckSuccess = ref<null | boolean>(null);

async function submit() {
  const api = useApiStore();

  console.log(`Logging in with ${username.value}`);
  if (username.value === null) {
    alert("Please fill the username");
    return;
  }
  if (password.value === null) {
    alert("Please fill the password");
    return;
  }
  localStorage.setItem("apiUrl", apiUrl.value);

  const appClient = new AllMyStorageApiClient({
    BASE: cutApiUrl(apiUrl.value),
  });

  const response = await appClient.auth.loginForAccessTokenApiV1AuthTokenPost({
    formData: {
      // grant_type: null,
      username: username.value,
      password: password.value,
      remember_me: rememberMe.value,
      // scope: null,
      // client_id: null,
      // client_secret, null,
    },
  });
  const accessToken = response.access.access_token;
  const imagesToken = response.images.access_token;
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("imagesToken", imagesToken);
  setAuthCookies(accessToken, imagesToken, rememberMe.value);
  api.setAccessToken(accessToken);
  api.setImagesToken(imagesToken);
  // The key of the encrypted passwords is a secret like the tokens: never part of a log line.
  if (response.encryption_key) {
    await storeMaster(response.encryption_key);
  } else {
    await clearMaster();
  }
  api.scheduleTokenRefresh();
  console.log(`Logged in with ${username.value}`, { access: describeToken(accessToken), images: describeToken(imagesToken) });
  api.fetchObjects();
  if (ionRouter.canGoBack()) {
    router.back();
  } else {
    await router.push({ name: "Root" });
  }
}
async function ping(currentApiUrl: string): Promise<boolean> {
  const appClient = new AllMyStorageApiClient({
    BASE: cutApiUrl(currentApiUrl),
  });

  try {
    const response = await appClient.status.pingApiV1StatusPingGet();
    return response === "pong";
  } catch (e) {
    console.error(e);
    return false;
  }
}

onMounted(async () => {
  apiCheckSuccess.value = await ping(apiUrl.value);
});

watch(() => apiUrl.value, async (newUrl): Promise<void> => {
  apiCheckSuccess.value = null;
  apiCheckSuccess.value = await ping(newUrl);
  reconfigureSentry(newUrl);
})

</script>

<style scoped>
p {
  padding-left: 20px;
}

ion-item > div {
  width: 100%;
}
</style>
